import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import type { TotpSecret } from 'firebase/auth';
import { darkTheme } from '../../lib/tokens';
import {
  setupTotpEnrollment,
  completeTotpEnrollment,
  signOutFirebase,
} from './firebaseAuth';
import { getFirebaseAuth } from '../../lib/firebase';

// Wird gezeigt, wenn der Backend-Login mit "multi-factor required" abgelehnt
// hat. Vorbedingung: User ist via Email/Passwort bei Firebase signed-in
// (Single-Factor). Nach erfolgreicher Enrollment loggen wir Firebase aus
// und schicken den User auf /login zurück — dort wird er beim nächsten
// Versuch mit der MFA-Challenge konfrontiert.

export function EnrollMfaPage(): JSX.Element {
  const navigate = useNavigate();
  const [enrollment, setEnrollment] = useState<{
    secret: TotpSecret;
    qrUrl: string;
    manualSecret: string;
  } | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    // Vorbedingung prüfen: Firebase-User muss signed-in sein. Falls nicht
    // (User hat Page direkt aufgerufen oder Session abgelaufen): zurück
    // zum Login.
    const auth = getFirebaseAuth();
    if (!auth.currentUser) {
      navigate('/login', { replace: true });
      return;
    }

    setupTotpEnrollment()
      .then(setEnrollment)
      .catch((err) => setError(humanError(err)));
  }, [navigate]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!enrollment) return;
    setError(null);
    setSubmitting(true);
    try {
      await completeTotpEnrollment(enrollment.secret, code);
      setDone(true);
      // Sign-out, damit der nächste Login einen frischen ID-Token mit
      // sign_in_second_factor-Claim liefert.
      await signOutFirebase();
      // 2 s Erfolgs-Feedback, dann redirect zum Login.
      setTimeout(() => {
        navigate('/login', {
          replace: true,
          state: { mfaEnrolled: true },
        });
      }, 2000);
    } catch (err) {
      setError(humanError(err));
      setCode('');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="flex min-h-screen items-center justify-center p-6"
      style={{
        background: `radial-gradient(120% 80% at 50% -10%, ${darkTheme.colors.accent}22 0%, transparent 55%), linear-gradient(180deg, #14142A 0%, ${darkTheme.colors.bgBase} 100%)`,
      }}
    >
      <div
        className="w-full max-w-[480px] rounded-2xl border p-6 shadow-2xl sm:p-8"
        style={{
          background: darkTheme.colors.bgSurface,
          borderColor: darkTheme.colors.borderSubtle,
        }}
      >
        <div className="mb-6">
          <div className="flex items-center gap-3">
            <div
              className="flex h-9 w-9 items-center justify-center rounded-lg font-extrabold text-white"
              style={{
                background: `linear-gradient(135deg, ${darkTheme.colors.accent} 0%, ${darkTheme.colors.accentLight} 100%)`,
              }}
            >
              C
            </div>
            <div className="flex flex-col leading-tight">
              <span className="text-base font-bold text-text-primary">Currivium</span>
              <span className="text-[10px] font-semibold uppercase tracking-widest text-accent-light">
                Admin
              </span>
            </div>
          </div>
        </div>

        {done ? (
          <div className="space-y-3">
            <h1 className="text-xl font-semibold text-success">2FA aktiviert ✓</h1>
            <p className="text-sm text-text-secondary">
              Du wirst gleich zum Login weitergeleitet. Beim nächsten Anmelden wird der Code aus
              deiner Authenticator-App abgefragt.
            </p>
          </div>
        ) : (
          <>
            <h1 className="mb-2 text-xl font-semibold text-text-primary">
              Zwei-Faktor-Authentifizierung einrichten
            </h1>
            <p className="mb-6 text-sm text-text-secondary">
              Admin-Zugang erfordert einen zweiten Faktor. Scanne den QR-Code mit einer
              Authenticator-App (Google Authenticator, 1Password, Bitwarden, Aegis …) und gib
              danach den 6-stelligen Code ein.
            </p>

            {!enrollment && !error && (
              <p className="text-sm text-text-tertiary">Lädt Setup …</p>
            )}

            {enrollment && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div
                  className="flex justify-center rounded-md p-4"
                  style={{ background: '#FFFFFF' }}
                >
                  <QRCodeSVG value={enrollment.qrUrl} size={200} level="M" />
                </div>

                <details className="text-xs">
                  <summary className="cursor-pointer text-text-tertiary hover:text-text-primary">
                    QR-Code lässt sich nicht scannen?
                  </summary>
                  <div
                    className="mt-2 rounded-md p-3 font-mono"
                    style={{ background: darkTheme.colors.bgBase, color: darkTheme.colors.textSecondary }}
                  >
                    Manueller Secret-Key:
                    <br />
                    <span className="break-all text-text-primary">{enrollment.manualSecret}</span>
                  </div>
                </details>

                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-text-secondary">
                    6-stelliger Code aus der App
                  </span>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    required
                    autoFocus
                    autoComplete="one-time-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    className="w-full rounded-md border bg-bg-base px-3 py-3 text-center text-lg tabular tracking-[0.4em] text-text-primary outline-none focus:border-accent"
                    style={{ borderColor: darkTheme.colors.borderSubtle }}
                  />
                </label>

                {error && <p className="text-sm text-danger">{error}</p>}

                <button
                  type="submit"
                  disabled={submitting || code.length !== 6}
                  className="w-full rounded-md py-2.5 text-sm font-semibold text-white transition disabled:opacity-50"
                  style={{ background: darkTheme.colors.accent }}
                >
                  {submitting ? 'Wird aktiviert …' : 'Aktivieren'}
                </button>

                <button
                  type="button"
                  onClick={async () => {
                    await signOutFirebase().catch(() => {});
                    navigate('/login', { replace: true });
                  }}
                  className="w-full rounded-md py-2 text-xs text-text-secondary hover:text-text-primary"
                >
                  Abbrechen
                </button>
              </form>
            )}

            {error && !enrollment && (
              <p className="mt-4 text-sm text-danger">{error}</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function humanError(err: unknown): string {
  const code = (err as { code?: string }).code;
  switch (code) {
    case 'auth/invalid-verification-code':
      return 'Code ungültig — bitte erneut aus der App eingeben.';
    case 'auth/requires-recent-login':
      return 'Sitzung abgelaufen — bitte erneut einloggen.';
    case 'auth/code-expired':
      return 'Code abgelaufen — neuen aus der App holen.';
    default:
      return (err as { message?: string }).message ?? 'Aktivierung fehlgeschlagen.';
  }
}
